import React, { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import clsx from 'clsx'
import { Container, Fade } from '@material-ui/core'
import { connect, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useMatomo } from '@datapunt/matomo-tracker-react'
import { getTaxons } from '../redux/actions/taxons'
import { getTranslations } from '../i18n'
import { setFullLocale } from '../utils/utils'
import Layout from '../components/Layout/Layout'
import Herosection from '../components/Hero/HeroSection'
import Catalog from '../components/Shop/Catalog'
import useShopStyles from '../components/Shop/Shop.style'
import Loader from '../components/Loader/Loader'
import { prismicText } from '../components/Global/CMSText'
import {
  emptyProductState,
  getCumulativeProductsByTaxonsSlug,
  getProductsByTaxonsSlug,
  setCurrentSlug,
  setCurrentSearch,
  filterProductsAction,
  filterCumulativeProductsAction,
} from '../redux/actions/products'
import { createClient, getSingleDocument, getByUID } from '../prismicio'
import { RootState } from '../redux/reducers/rootReducer'
import { PrismicPage } from '../interfaces/common/prismicPage'
import { PrismicShop } from '../interfaces/common/prismicShop'
import { SAVE_PRISMIC_DOCUMENTS } from '../redux/actions/prismic'
import { InferGetStaticPropsType } from 'next/types'
import { GetMapDispatchTypes } from '../interfaces/common/redux'
import * as gtag from '../utils/gtag'

const mapStateToProps = (state: RootState) => ({
  taxons: state.taxons,
  cart: state.cart,
  products: state.products,
  stock: state.stock,
  parameters: state.parameters,
})

const mapDispatchToProps = {
  getTaxons: getTaxons,
  getProductsByTaxonsSlug: getProductsByTaxonsSlug,
  getCumulativeProductsByTaxonsSlug: getCumulativeProductsByTaxonsSlug,
  setCurrentSlug: setCurrentSlug,
  setCurrentSearch: setCurrentSearch,
  emptyProductState: emptyProductState,
  filterProductsAction: filterProductsAction,
  filterCumulativeProductsAction: filterCumulativeProductsAction,
}

type TSearchProps = ReturnType<typeof mapStateToProps> &
  InferGetStaticPropsType<typeof getServerSideProps> &
  GetMapDispatchTypes<typeof mapDispatchToProps>

const Shop = ({
  doc,
  basket,
  takeback,
  inStoreText,
  main,
  funnel,
  classic_sub,
  filterProductsAction,
  filterCumulativeProductsAction,
  getCumulativeProductsByTaxonsSlug,
  setCurrentSearch,
  getProductsByTaxonsSlug,
  cart,
  stock,
  pageData,
  products,
  accountRes,
  faqRes,
  banner,
}: TSearchProps) => {
  const { t } = useTranslation()
  const router = useRouter()
  const [search] = useState(router.query.q)
  const isSubscription =
    process.env.STORE_TYPE === 'subscription' ||
    process.env.STORE_TYPE === 'classic_subscription'
  const classes = useShopStyles({})
  const { trackPageView, trackEvent } = useMatomo()
  const dispatch = useDispatch()
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isLoadingProductsAndStocks, setIsLoadingProductsAndStocks] =
    useState<boolean>(true)
  const page =
    typeof router?.query?.currentPage !== 'undefined'
      ? typeof router?.query?.currentPage === 'string'
        ? router?.query?.currentPage
        : router?.query?.currentPage?.length > 0 &&
          router?.query?.currentPage[0]
      : '1'
  const [currentPage, setCurrentPage] = useState(
    typeof page !== 'undefined' ? parseInt(page) : 1
  )
  const [isHandleMore, setIsHandleMore] = useState(false)
  const columnLayout = main?.data?.page_shop_layout
  const settings = main?.data
  const isMobileAccordion = doc?.data?.is_display_accordion_on_mobile_page
  const loadMore = doc?.data?.pagination_mode
  const productToLoadLimit =
    doc?.data?.product_limit_per_load !== null
      ? doc?.data?.product_limit_per_load
      : 48

  const getProducts = (isCumulative = false, nextPage = false) => {
    const filtersFromUrl = router.query.filters
    const page = nextPage ? currentPage + 1 : currentPage
    if (isCumulative) {
      if (process.env.NEXT_PUBLIC_ENABLE_ALGOLIA === 'true') {
        filterCumulativeProductsAction(
          Array.isArray(search) ? search.join(' ') : search,
          page,
          productToLoadLimit,
          router.locale
        )
      } else {
        getCumulativeProductsByTaxonsSlug(
          null,
          productToLoadLimit,
          page,
          filtersFromUrl ? JSON.parse(filtersFromUrl as string) : [],
          search
        )
      }
    } else {
      if (process.env.NEXT_PUBLIC_ENABLE_ALGOLIA === 'true') {
        filterProductsAction(
          Array.isArray(search) ? search.join(' ') : search,
          page,
          productToLoadLimit,
          router.locale
        )
      } else {
        getProductsByTaxonsSlug(
          null,
          48,
          page,
          filtersFromUrl ? JSON.parse(filtersFromUrl as string) : [],
          search
        )
      }
    }
  }

  useEffect(() => {
    dispatch(
      SAVE_PRISMIC_DOCUMENTS({
        general: main?.data,
        funnel: funnel?.data,
        basket: basket?.data,
        instore: inStoreText?.data,
      })
    )
  }, [funnel, main, basket, inStoreText])

  useEffect(() => {
    trackPageView({ documentTitle: 'Search' })
    trackEvent({
      category: `Page Search`,
      action: `User search for ${search}`,
    })
    gtag.tracker('searchPage', { value: search })
  }, [])

  useEffect(() => {
    localStorage.removeItem('Filters')
    if (!isHandleMore && search !== products.currentSearch) {
      setCurrentSearch(search)
      if (loadMore && products?.items?.items?.length === 0) {
        getProducts(true)
      } else {
        getProducts()
      }
    }
    setIsHandleMore(false)
  }, [products?.items?.items?.length])

  const heroInfo = useMemo(() => {
    // Parse data from the received page data (if there's a page with the current slug)
    return {
      title: prismicText(pageData?.data.title).toUpperCase(),
      // Check if a hero section has been added to the page, and get its background image
      image: pageData?.data?.body?.find(
        (el) => el.slice_type === 'hero_section'
      )?.primary?.background,
    }
  }, [pageData?.data])

  const handlePrev = () => {
    setCurrentPage(currentPage - 1)
    window?.scrollTo(0, 0)
    const pageNumber = currentPage - 1
    router.push(
      {
        pathname: '/search',
        query: { q: search, currentPage: pageNumber },
      },
      undefined,
      { shallow: true }
    )
  }

  const handleNext = () => {
    setCurrentPage(currentPage + 1)
    window?.scrollTo(0, 0)
    const pageNumber = currentPage + 1
    router.push(
      {
        pathname: '/search',
        query: { q: search, currentPage: pageNumber },
      },
      undefined,
      { shallow: true }
    )
  }

  const handleLoadMore = () => {
    setIsHandleMore(true)
    setCurrentPage(currentPage + 1)
    getProducts(true, true)
  }
  useEffect(() => {
    if (!products.loading && !stock.loading) {
      setIsLoadingProductsAndStocks(false)
    } else {
      if (!isLoadingProductsAndStocks) {
        setIsLoadingProductsAndStocks(true)
      }
    }
  }, [products.loading, stock.loading])

  return (
    <Layout
      mainLogo={main?.data?.logo}
      doc={main}
      t={t}
      inStoreText={inStoreText}
      metaTitle={prismicText(doc?.data?.meta_title_shop)}
      metaDescription={prismicText(doc?.data?.meta_description_shop)}
      metaKeywords={doc?.data?.meta_keywords_shop}
      basket={basket}
      takeback={takeback}
      account={accountRes}
      faq={faqRes}
      banner={banner}
    >
      <main className={clsx(`main main--shop ${classes.root}`, 'search-page')}>
        <Herosection
          title={t('texts:search_page:title', {
            search,
            length: products?.items?.total,
          })}
          isMainTitle
          heroImg={heroInfo.image || main?.data?.hero_banner}
          heroSettings={main?.data}
          isDisplayAccordionOnMobile={isMobileAccordion}
        />
        <section className={clsx('shop__content', classes.shopContent)}>
          <Container maxWidth="xl" className={classes.shopContainer}>
            <Container
              maxWidth="xl"
              className={clsx(
                'shop__area',
                classes.shopArea,
                isSubscription && columnLayout && classes.shopAreaColumn
              )}
              data-stock={stock.loading ? 'loading' : 'loaded'}
            >
              {cart.loading && (
                <div className={classes.loader}>
                  <Loader />
                </div>
              )}
              <>
                {isLoadingProductsAndStocks && currentPage === 1 && (
                  <Fade in>
                    <div className={classes.loader}>
                      <Loader />
                    </div>
                  </Fade>
                )}
                <Catalog
                  products={products}
                  loadMore={loadMore}
                  handleLoadMore={handleLoadMore}
                  setIsDrawerOpen={setIsDrawerOpen}
                  currentPage={currentPage}
                  handlePrev={handlePrev}
                  handleNext={handleNext}
                  classic_sub_text={classic_sub}
                  shopData={doc}
                  mainData={main}
                  hideFilters={true}
                  settings={settings}
                  hidePrice={settings?.only_add_with_pack}
                  basket={basket}
                  cart={cart}
                  takeback={takeback}
                  isDisplayedPrice={main?.data?.is_product_price_displayed}
                  isLoadingProductsAndStocks={isLoadingProductsAndStocks}
                  hideAvailableProductsOnly={true}
                  isSearch={true}
                />
              </>
            </Container>
          </Container>
        </section>
      </main>
    </Layout>
  )
}

export async function getServerSideProps({ params, locale, previewData }) {
  const client = createClient({ previewData })
  const lang = setFullLocale(locale)

  let page: PrismicPage | null = null
  try {
    // Gets the page that has the same UID as the current slug
    page = await getByUID(client, 'pages', params.slug?.[0], lang)
  } catch (_e) {}
  const shop: PrismicShop | null = await getSingleDocument(client, lang, 'shop')
  const basket = await getSingleDocument(client, lang, 'basket')
  const general = await getSingleDocument(client, lang, 'general')
  const funnel = await getSingleDocument(client, lang, 'funnel')
  const takeback = await getSingleDocument(client, lang, 'take_back')
  const inStoreText = await getSingleDocument(client, lang, 'instore')
  const classic_sub = await getSingleDocument(
    client,
    lang,
    'classic_subscription_spec'
  )
  const accountRes = await getSingleDocument(client, lang, 'account')
  const faqRes = await getSingleDocument(client, lang, 'faq')
  const banner = await getSingleDocument(client, lang, 'banner')

  return {
    props: {
      takeback,
      doc: shop,
      basket,
      main: general,
      pageData: page,
      inStoreText,
      funnel,
      accountRes,
      faqRes,
      classic_sub,
      banner,
      i18nResources: await getTranslations(locale, ['texts']),
    },
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Shop)
